/* eslint-disable @typescript-eslint/no-unused-vars */
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// TYPES & INTERFACES (MASTER-DEFINITION)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

// 💉 CHIRURGIE: Import des Betty-Slice-Typs
import type { PaketTeilleistung } from '../types/types';
import { BettySliceType } from './slices/bettySlice';
import { PremiumSliceType } from './slices/premiumSlice';
import { PeProfitSliceType } from './slices/peProfitSlice';

// --- SNAPSHOT & LOCK SYSTEM (EISERNE KLAMMER V143) ---
export interface LockedMetricsSnapshot {
    isLocked: boolean;
    lockedAt?: string;
    lockedBy?: string;
    metrics: {
        sollumsatz?: number;
        brutto?: number;
        netto?: number;
        [key: string]: any; 
    };
}

export interface MonatsFestsetzungSnapshot {
    isLocked: boolean;
    lockedAt?: string;
    lockedBy?: string;
    arbeitstage: number;
    wochenstunden: number;
    lohnfaktor: number;
    sollUmsatzMonat: number;
    effektiverBruttolohn: number;
    stdLohnGrund: number;
    stdLohnEffektiv: number;
    stdLohnGesamt: number;
    basisMetrics?: Record<string, any>;
    bonusFaktoren?: { bedienung: number; vk: number };
}

export interface MitarbeiterLockedMetricsIndex {
    halbjahr1?: Record<string, MonatsFestsetzungSnapshot>;
    halbjahr2?: Record<string, MonatsFestsetzungSnapshot>;
}

export interface UnternehmerMonatsLockedEntry {
    festmachen?: boolean;
    lockedSnapshot?: {
        isLocked?: boolean;
        lockedAt?: string;
        lockedBy?: string;
        metrics?: {
            monatsstunden?: number;
            planumsatz?: number;
            mwst?: number;
            kostendeckung?: number;
            arbeitstage?: number;
            wochenstunden?: number;
            stdLohn?: number;
            sollumsatzPStd?: number;
        };
    };
}

export interface UnternehmerLockedMetricsIndex {
    halbjahr1?: Record<string, UnternehmerMonatsLockedEntry>;
}

export type MitarbeiterStatus = 'ok' | 'warnung' | 'schlecht' | 'neutral';

export interface MonatsDaten { 
    monat: string; 
    bedienUmsatz: number; 
    vkUmsatz: number; 
    urlaubsTage: number; 
    krankheitsTage: number; 
    unproduktiveStunden: number; 
    unproduktiveStundenPlus: boolean; 
    ueberstundenAuszahlen: boolean; 
    festmachen: boolean; 
}

export interface ULMonatsDaten { 
    monat: string; 
    bedienumsatzBrutto: number; 
    vkUmsatzBrutto: number; 
    urlaubsTage: number; 
    krankheitsTage: number; 
    festmachen: boolean; 
    lockedSnapshot?: LockedMetricsSnapshot; 
    [key: string]: any; 
}

export type EmployeeCountryCode = 'DE' | 'IT_BZ' | 'AT' | 'CH';

export interface EmployeeData { 
    id: string; 
    name: string; 
    grundgehalt: number; 
    lohnzusatz: number; 
    bonus: number; 
    wochenstunden: number; 
    arbeitstage: number; 
    taetigkeit: string; 
    zugehoerigkeit: string; 
    monatsstunden: number; 
    grundlohn: number; 
    bruttolohnGesamt: number; 
    stundenlohnGrund: number; 
    stundenlohnGesamt: number; 
    lohnnebenkostenSatz: number; 
    lohnnebenkosten: number; 
    totalCost: number; 
    hasGeraete?: boolean;
    /** Lohn-Ländermodus ('DE' Standard | 'IT_BZ' Südtirol Netto-Modell | 'AT' Österreich KV) */
    country?: EmployeeCountryCode | string;
    /** Mitarbeiter-Kategorie ('produktiv' | 'unproduktiv' | 'azubi') */
    kategorie?: 'produktiv' | 'unproduktiv' | 'azubi' | string;
    /** Bruttolohn (DE / AT) */
    grossWage?: number;
    /** Vereinbarter Nettolohn "Netto in busta" (IT_BZ) */
    netWage?: number;
    /** Optionaler StB-Override für reale AG-Gesamtkosten (Costo Totale Aziendale) */
    costoAziendaleOverride?: number;
    /** Normalisierte AG-Gesamtkosten aus calculateTotalPersonnelCost */
    totalPersonnelCost?: number;
    /** Live-Anzeige AG-Gesamtkosten in der Mitarbeitertabelle */
    agGesamt?: number;
    /** ID des verknüpften Mitarbeiters aus personalData (bei 'Unprod. Anteil') */
    linkedProductiveEmployeeId?: string;
    /** true = Betrag/Stunden werden oben beim produktiven MA abgezogen (Split-Abzug) */
    isSplitDeduction?: boolean;
    /** Ursprüngliches Vertrags-Grundgehalt vor Split-Abzug */
    rawGrundgehalt?: number;
    /** Ursprüngliche Vertrags-Wochenstunden vor Split-Abzug */
    rawWochenstunden?: number;
    /** Abgezogenes Grundgehalt durch 'Unprod. Anteil' (Split-Abzug) */
    splitDeductedGrundgehalt?: number;
    /** Abgezogene Wochenstunden durch 'Unprod. Anteil' (Split-Abzug) */
    splitDeductedWochenstunden?: number;
}

/** Team-Leistungslohn V1.1 — Persistenz unter clientData.teamBonus */
export type TeamBonusMode = 'individual' | 'team';

export interface TeamBonusConfig {
    bonusMode: TeamBonusMode;
    poolAmount: number;
    selectedMemberIds: string[];
}

export const DEFAULT_TEAM_BONUS: TeamBonusConfig = {
    bonusMode: 'individual',
    poolAmount: 0,
    selectedMemberIds: [],
};

export interface FrozenStammdatenHJ1 {
    name: string;
    wochenstunden: number;
    arbeitstage: number;
    grundgehalt: number;
    lohnzusatz: number;
    bonus: number;
    taetigkeit: string;
    zugehoerigkeit?: string;
    arbeitetMitGeraeten: boolean;
    selectedDeviceIds?: string[];
    country?: 'DE' | 'IT_BZ' | string;
    kategorie?: string;
    grossWage?: number;
    netWage?: number;
    costoAziendaleOverride?: number;
    /** Lohnfaktor zum Sperrzeitpunkt — verhindert Drift der Soll-Kennzahlen */
    lohnfaktorAktiv?: number;
    kostendeckungsfaktor1?: number;
    provisionErstesHalbjahr: {
        provisionDienstleistung?: number;
        provisionVerkauf?: number;
    };
}

/** Stammdaten-Snapshot Unternehmer beim HJ1-Abschluss */
export interface FrozenUnternehmerStammdatenHJ1 {
    name: string;
    prodWochenstunden: number;
    prodWochentage: number;
    prodUnternehmerlohn: number;
    planumsatzBrutto: number;
    kostendeckungsumsatz: number;
    monatsstunden: number;
    stdLohn: number;
    sollumsatzPStd: number;
    sollumsatzPStd75: number;
    sollumsatzProTag: number;
    arbeitszeitTag: number;
    wochensatz: number;
    kostendeckungsfaktor1: number;
    partners?: Record<string, {
        name: string;
        prodWochenstunden: number;
        prodWochentage: number;
        prodUnternehmerlohn: number;
        planumsatzBrutto: number;
        kostendeckungsumsatz: number;
        monatsstunden: number;
        stdLohn: number;
        sollumsatzPStd: number;
        sollumsatzPStd75: number;
        sollumsatzProTag: number;
        arbeitszeitTag: number;
    }>;
}

export interface MitarbeiterLeistungData { 
    halbjahr1: MonatsDaten[]; 
    halbjahr2: MonatsDaten[]; 
    calculatedAvgVkFaktor?: number; 
    calculatedSumKrankTage?: number; 
    calculatedAvgEfficiency?: number; 
    calculatedAvgUmsatzauslastung?: number; 
    calculatedStatusHJ1?: MitarbeiterStatus; 
    calculatedStatusHJ2?: MitarbeiterStatus;
    selectedDeviceIds?: string[];
    lockedMetrics?: MitarbeiterLockedMetricsIndex | LockedMetricsSnapshot;
    frozenStammdatenHJ1?: FrozenStammdatenHJ1;
    lockedMetrics_string?: string;
    frozenStammdatenHJ1_string?: string;
}

// --- KALKULATOR BASIS KOMPONENTEN ---
export interface Geraet {
    id: string;
    name: string;
    kostenProzent: number; 
}

export interface MaterialItem {
    id: string;
    name: string;
    gebinde: number;
    ekPreis: number;
}

export interface SurchargeItem {
    id: string;
    description: string;
    price: number;
}

export interface PriceParameter {
    id: string;
    desc: string;
    preis: number;
    ruecklage: number;
    anpassung: number;
}

// --- EXTENSIONS KALKULATOR SPEZIFISCH ---
export interface ExtensionsKalkulatorExtension {
    id: string;
    name: string;
    packContent: string;
    piecesPerPack: number;
    ekNetto: number;
    // 💉 CHIRURGIE-FIX: Tippfehler behoben! (verkaufsMarge statt verkausMarge)
    verkaufsMarge: number;
}

export interface ExtensionsKalkulatorInputs {
    menge: number;
    stundensatz: number;
    einarbeitungszeit: number;
}

export interface ExtensionsKalkulatorState {
    inputs: ExtensionsKalkulatorInputs;
    extensions: ExtensionsKalkulatorExtension[];
    selectedExtensionId: string | null;
}

// --- PRODUKT RECHNER SPEZIFISCH ---
export interface ProduktRechnerItem {
    id: string;
    name: string;
    ek: number;
    vk: number;
}

export interface ProduktRechnerData {
    zielGewinnVk: string;
    zielUeberschuss: string;
    zielBedienzeit: string;
    produkte: ProduktRechnerItem[];
}

// --- PREISLISTEN GENERATOR (KI-READY) ---
export type PreislistenKategorie = 
  | 'Damen' | 'Herren' | 'Farbe' | 'Strähnen' 
  | 'Kosmetik' | 'Dauerwelle' | 'Spezial' | string; 

export interface PreislistenEintrag {
  id: string;                  
  name: string;                
  kategorie: PreislistenKategorie;
  bedienzeit: number;          
  
  // Material-Daten
  materialEinsatz: number;       
  materialMenge?: number;        
  materialBeschreibung?: string; 
  
  // Geräte-Daten
  geräteEinsatz?: number;
  geräteBeschreibung?: string;

  finalerPreis: number;        
  preisProStunde: number;      
  createdAt: number; 
  
  // 💉 HIER SIND DIE FEHLENDEN KI-METRIKEN (Rücklagen)
  gewinn?: number;
  gewinnPercent?: number;

  /** true = Paket aus mehreren Teilleistungen. Fehlt bei klassischen Einzeleinträgen. */
  isPackage?: boolean;
  packageServices?: PaketTeilleistung[];
  /** Summe der Teilleistungs-Minuten. Bei Paketen identisch zu bedienzeit. */
  gesamtDauerMinuten?: number;
}

export interface PreislistenState {
  // Das Interface bleibt leer, da firebase es jetzt regelt, dient nur der Kompatibilität
}

// --- DRAFT SYSTEM (ÄNDERUNGSZENTRALE) ---

/** Immutable Chronik-Eintrag (Coach-Lock in der Änderungszentrale) */
export interface AenderungsChronikEintrag {
  createdAt: string;
  authorName: string;
  description: string;
  draftId?: string;
  category?: string;
}

// 1. BESTANDSMITARBEITER
export interface StaffDraftItem {
  id: string;
  originalId?: string; 
  inputs: any;
  calculations: any;
  hasChanges: boolean;
  isValid: boolean;
  isLocked: boolean;
  lockedBy?: string; 
  lockedAt?: string; 
}

// 2. NEUE MITARBEITER
export interface NewStaffDraftItem {
    id: string;
    inputs: any;
    calculations: any;
    isComplete: boolean;
    isLocked: boolean;
    lockedBy?: string;
    lockedAt?: string;
}

// 3. UNTERNEHMER / INHABER DRAFT
export interface EntrepreneurDraftItem {
    id: string;
    inputs: any;
    calculations: any;
    isComplete: boolean;
    hasChanges: boolean;
    isLocked: boolean;
    lockedBy?: string;
    lockedAt?: string;
}

// 4. MANDANTENDATEN (STAMMDATEN) DRAFT
export interface ClientMasterDraftItem {
    id: string;
    inputs: any;
    isComplete: boolean;
    hasChanges: boolean;
    isLocked: boolean;
    lockedBy?: string;
    lockedAt?: string;
}

// 5. UNTERNEHMENSKOSTEN
export interface CostDraftItem {
    id: string;
    inputs: any;
    calculations: any;
    isComplete: boolean;
    isLocked: boolean;
    lockedBy?: string;
    lockedAt?: string;
}

// 6. ÖFFNUNGSZEITEN (NEU)
export interface OpeningHoursDraftItem {
    id: string;
    inputs: any;
    isComplete: boolean;
    hasChanges: boolean;
    isLocked: boolean;
    lockedBy?: string;
    lockedAt?: string;
}

// --- BUDGET PLANER SPEZIFISCH ---
export interface BudgetEntry {
    id: string;
    istwertBwa: string;
}

export interface BudgetPlanerState {
    zeitraum: string;
    entries: BudgetEntry[];
}

// --- PRIVATKOSTEN KOMPASS SPEZIFISCH ---
export interface PrivateItem {
    name: string;
    value: number;
    frequency?: number; 
    fixed: boolean;
}

export interface PrivateCategory {
    id: string;
    type: 'living' | 'social';
    title: string;
    items: PrivateItem[];
}

export interface PrivateCostState {
    targetLiving: number;
    targetSocial: number;
    monthlyCats: PrivateCategory[];
    periodicItems: PrivateItem[];
}

// --- UNTERNEHMER & BWA ---
export interface UnternehmerData { 
    entnahme?: number; 
    sozialkosten?: number; 
    einkommenssteuer?: number; 
    prodWochenstunden?: number; 
    geoeffneteStunden?: number[]; 
    monatsUmsaetze?: number[]; 
    durchschnittsumsatz?: number; 
    prodWochentage?: number; 
    /** GmbH-GF-Rechner: Zielgehalt (EUR/Monat) */
    gfZielgehalt?: number | string;
    /** GmbH-GF-Rechner: Umsatz am Kunden (EUR/Monat) */
    gfUmsatz?: number | string;
    lockedMetrics?: LockedMetricsSnapshot;
    unternehmerLockedMetrics?: UnternehmerLockedMetricsIndex;
    unternehmerLockedMetrics_string?: string;
}

export interface PrognoseZeile { 
    id: string; 
    mitarbeiterId: string; 
    lohn: number; 
    faktor: number; 
    jahresumsatz: number; 
    datum: string; 
    aktiv: boolean; 
}

export interface BwaData { 
    zeitraum: number; 
    umsatz?: number; 
    material?: number; 
    prognoseZeilen: PrognoseZeile[]; 
    prognoseBruttolohn?: number; 
    prognoseFaktor?: number; 
    visibleGeraeteCount?: number;
    [key: string]: any; 
}

export interface LohnfaktorData { 
    unternehmerStundenlohn_editable?: number; 
    /** true = Nutzer justiert Soll manuell; false/undefined = Auto-Konvergenz */
    isManualStundenlohnActive?: boolean;
    sicherheitsfaktor?: number; 
    manualLohnfaktor?: number; 
    kostendeckungsfaktor1?: number; 
    kostendeckungsfaktor2?: number; 
    mindestumsatz1?: number; 
    mindestumsatz2?: number; 
}

export interface SwissVatSettings {
    standardRate: number;      // Default: 8.1
    vatThreshold: number;      // Default: 100000
    isVatExempt: boolean;       // Dynamischer Status
    voluntaryOption: boolean;  // Manuelle Optierung bei < 100k
}

// --- SETTINGS & LAND ---
export interface CountryData { 
    name: string; 
    Mindestlohn: number; 
    Minijob_Lohn_Grenze: number; 
    Lohnnebenkosten: number; 
    Lohnnebenkosten_Minijob: number; 
    MwSt_Satz: number; 
    currency: 'EUR' | 'CHF'; 
    ItBz_Faktor_Azubi?: number;
    ItBz_Faktor_Teilzeit?: number;
    ItBz_Faktor_Vollzeit?: number;
    ItBz_Vollzeit_Stundenschwelle?: number;
    At_Lohnnebenkosten_Fachkraft?: number;
    At_Lohnnebenkosten_Azubi?: number;
    vatThreshold?: number;
    isVatExempt?: boolean;
    voluntaryOption?: boolean;
}

export interface GlobalSettings { 
    [key: string]: any; 
    Tagessatz: number; 
    Stundensatz: number; 
    KmRate: number; 
    Warenwirtschaftsaufschlag_VK: number; 
    Warenwirtschaftsaufschlag_DL: number; 
    Urlaubsausgleich: number; 
    Grobkundenaufschlag: number; 
    Grobkundenaufschlag_ab: number; 
    Insider_Solo: number; 
    Insider_Team: number; 
    basicStandardPreis: number; 
    Neukunden_Aufschlag?: number;
    geraeteListe?: Geraet[]; 
    Investlinie: number; 
    Gewinnlinie: number; 
    Zusatzumsatz: number; 
    Verlust_1: number; 
    Liquiditaet: number; 
    Wochensatz: number; 
    Auslastungsfaktor: number; 
    Auslastungsfaktor_Max: number; 
}

export interface SettingsState { 
    selectedCountryId: 'DE' | 'AT' | 'CH' | 'IT'; 
    global: GlobalSettings; 
    webdesignPackages: { [key: string]: number }; 
    countries: { [key: string]: CountryData }; 
    countryOverrides: Record<string, Partial<CountryData>>; 
    country: Partial<CountryData>;
    materialListe: MaterialItem[];
    surchargeListe: SurchargeItem[];
    priceParameterListe: PriceParameter[];
    /** Academy Download-Materialien (optional; fehlende Keys werden gemerged) */
    downloadItems?: DownloadItem[];
    downloadCategories?: TutorialCategorySettings[];
    tutorialVideos?: VideoTutorialSettings[];
    tutorialCategories?: TutorialCategorySettings[];
    /**
     * Seitenhilfe: pageRegistry-key → tutorialVideos[].id
     * Salonübergreifend in system_data/global_settings.settings
     */
    pageVideoHelp?: Record<string, string>;
}

/** Academy Video (settings.tutorialVideos) */
export interface VideoTutorialSettings {
    id: string;
    title: string;
    description: string;
    src: string;
    category?: string;
    /** Optionales Vorschaubild (Firebase/CDN); sonst Vimeo/YouTube-Player-Thumbnail */
    thumbnailUrl?: string;
    /**
     * Sichtbar in der Academy für Mandanten.
     * Fehlt das Feld (Legacy) = öffentlich. Neu angelegt standardmäßig false.
     */
    isPublic?: boolean;
}

/** Academy Rubrik */
export interface TutorialCategorySettings {
    name: string;
}

/** Academy Download-Material (settings.downloadItems) */
export type DownloadFileType = 'pdf' | 'zip' | 'xlsx' | 'image' | 'other';

export interface DownloadItem {
    id: string;
    title: string;
    description: string;
    fileUrl: string;
    fileType: DownloadFileType;
    category?: string;
    fileName?: string;
    /** Optionales Vorschaubild (Academy-Karte); bei Bild-Dateien oft = fileUrl */
    thumbnailUrl?: string;
    uploadedAt: string;
}

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// PAYOUT-ENGINE & CLEARING (EISERNE KLAMMER V148)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export type TransactionType = 'cost' | 'provision';

export interface ClearingTransaction {
    id: string;
    type: TransactionType;
    date: string;         // ISO Date String
    amount: number;       // Brutto-Betrag der Auslage oder Provision
    description: string;  // Verwendungszweck (z.B. "Software-Lizenz", "Provision Beauty")
    assignedToId: string; // TeamMember ID (Derjenige, der das Geld ausgelegt oder die Provision erhalten hat)
}

export interface InvoiceRecord {
    id: string;           // Generiert: [Prefix][MM][YYYY] (z.B. KN042026)
    coachId: string;      // TeamMember ID
    date: string;         // Rechnungsdatum
    amountNet: number;
    taxRate: number;      // 19 oder 0 (für Support/Non-EU)
    amountGross: number;
    status: 'ausstehend' | 'bezahlt' | 'storniert';
    periodMonth: string;  // Abrechnungsmonat (z.B. "04")
    periodYear: string;   // Abrechnungsjahr (z.B. "2026")
}

export interface PayoutEngineState {
    transactions: ClearingTransaction[];
    invoices: InvoiceRecord[];
}

// --- TEAM & APP ---
export interface TeamMember {
    id: string;
    name: string;
    role: string;
    imageUrl: string;
    description: string;
    email: string;
    phone: string;
    category: 'coach' | 'webdesign' | 'support';
    invoicePrefix?: string;  // z.B. 'KN', 'SO', 'KM', 'KJ'
    invoiceLogoUrl?: string; // Dateiname im Asset-Ordner, z.B. 'skaadi-nelly.png'
}

export interface AppSettings { logoUrl: string; basicLogoUrl: string; }

export interface Snapshot { id: string; name: string; timestamp: number; data: ClientDataState; }

// --- 💉 CHIRURGIE-FIX: NEUES INTERFACE FÜR PERSONENGESELLSCHAFT ---
export interface PersonengesellschaftPartner {
    id: string;
    name: string;
    entnahme?: number; 
    sozialkosten?: number; 
    einkommenssteuer?: number; 
    prodWochenstunden?: number; 
    monatsUmsaetze?: number[]; 
    durchschnittsumsatz?: number; 
    prodWochentage?: number; 
    ulHalbjahr1?: ULMonatsDaten[]; // Hinzugefügt
    ulHalbjahr2?: ULMonatsDaten[]; // Hinzugefügt
}

/** Eintrag in der Root-Filial-Registry (kalkulation_mandanten/{uid}) */
export interface BranchRegistryEntry {
    id: string;
    name: string;
    isMain: boolean;
    createdAt: string;
}

// --- ZENTRALER MANDANTEN STATUS ---
export interface ClientDataState { 
    firmenname: string; 
    mandantNr: string; 
    betreuer: string; 
    useAlternativeLogo: boolean; 
    isVatExempt: boolean; 
    unternehmerData: UnternehmerData; 
    bwaData: BwaData; 
    personalData: Partial<EmployeeData>[]; 
    unproductivePersonalData: Partial<EmployeeData>[]; 
    azubiAssistenzData: Partial<EmployeeData>[]; 
    /** Team- vs. Einzel-Leistungslohn (V1.1) */
    teamBonus?: TeamBonusConfig;
    lohnfaktorData: LohnfaktorData; 
    mitarbeiterLeistung: { [employeeId: string]: Partial<MitarbeiterLeistungData> }; 
    lizenzDetails: { typ: 'basic' | 'insider'; laeuftAb: string | null; }; 
    isHJ2Freigegeben: boolean; 
    isHJ1GesperrtGlobal: boolean; 
    isCostAdjustmentRequired: boolean;
    /** Stammdaten-Unternehmer zum Zeitpunkt der HJ1-Sperre */
    frozenUnternehmerStammdatenHJ1?: FrozenUnternehmerStammdatenHJ1;
    frozenUnternehmerStammdatenHJ1_string?: string; 
    gesamtleistungData: any[]; 
    gesamtleistungSettings: { planStundenTag: number }; 
    urlaubsverlustData: any[]; 
    ulBasisdaten: { simulationsStundenTag: number }; 
    ulHalbjahr1: ULMonatsDaten[]; 
    ulHalbjahr2: ULMonatsDaten[]; 
    soloVacationDays?: number;
    isSoloBusiness?: boolean;
    kontoinhaberIsSameAsUnternehmer?: boolean;
    unternehmername?: string;
    /** Filialname / Standortbezeichnung (optional) */
    branchName?: string;
    /** Filialbetrieb aktiv (Root-Registry) */
    hasBranches?: boolean;
    /** Filial-Liste (Root-Registry) */
    branches?: BranchRegistryEntry[];
    /** Aktive Filiale für Load/Save-Isolation */
    activeBranchId?: string;
    /** Summe Netto-Monatsumsatz aller Filialen (TeamPro+-Schwelle) */
    aggregatedNettoMonatsumsatz?: number;
    /** Persistierter Netto-Monatsumsatz für Tarif-Auflösung (bei Filialen = Aggregat) */
    nettoMonatsumsatz?: number;
    /** TeamPro+ automatisch / filialübergreifend freigeschaltet */
    isTeamProPlus?: boolean;
    zieleStatus?: Record<string, string>;
    zieleCoachNotiz?: string;
    pageVisibility?: { [key: string]: boolean };
    /** Persistierte Premium-Käufe (z.B. 'pe-papers', 'pe-profit') — leer = nicht gekauft */
    unlockedPremiumTools?: string[];
    /** Master-PIN Seiten-Sperre (Root-persistiert, überlebt Jahreswechsel) */
    pinLockConfig?: {
        isPinActive: boolean;
        pinHash: string;
        lockedPageIds: string[];
        updatedAt?: string;
    };
    kalkulator?: any;
    extensionsKalkulator: ExtensionsKalkulatorState; 
    produktRechnerData: ProduktRechnerData; 
    budgetPlaner: BudgetPlanerState; 
    privateCostData: PrivateCostState; 
    gesamtleistungLockedMetrics?: LockedMetricsSnapshot;
    vorjahresBwaData?: any;
    vorjahresUnternehmerData?: any;
    vorjahresPersonalData?: any;
    vorjahresCalcData?: any;

    // 💉 CHIRURGIE-FIX: EINBINDUNG DER PERSONENGESELLSCHAFT
    personengesellschaft?: {
        aktiv: boolean;
        partner: PersonengesellschaftPartner[];
    };

    wochensatz?: number;
    auslastungsfaktor?: number;
    auslastungsfaktorMax?: number;
    zusatzumsatz?: number;
    gewinnlinie?: number;
    investlinie?: number;
    verlust1?: number;
    liquiditaet?: number;
    
    drafts: {
        existingStaff: Record<string, StaffDraftItem>; 
        newStaff: NewStaffDraftItem[]; 
        entrepreneur: EntrepreneurDraftItem[]; 
        clientMaster: ClientMasterDraftItem[]; 
        costs: CostDraftItem[]; 
        openingHours: OpeningHoursDraftItem[]; 
    };

    /** Permanente Chronik gesperrter Änderungszentrale-Einträge (nicht beim Unlock gelöscht) */
    aenderungsChronik?: AenderungsChronikEintrag[];

    preisliste?: PreislistenEintrag[];

    [key: string]: any; 
}

export interface StoredState { 
    clientData: ClientDataState; 
    settings: SettingsState; 
    team: TeamMember[]; 
    teamRegistry?: TeamMember[];
    appSettings: AppSettings; 
    savedSnapshots: Snapshot[]; 
}

export interface HistoryState {
    clientData: ClientDataState;
    settings: SettingsState;
}

// 💉 CHIRURGIE: BettySliceType zur AppState Extension hinzugefügt
export interface AppState extends StoredState, PreislistenState, BettySliceType, PremiumSliceType, PeProfitSliceType {
    past: HistoryState[];
    future: HistoryState[];
    isInitialized: boolean;
    showFormulas: boolean;
    zoomLevel: number;
    
    isSystemBooted: boolean;
    setSystemBooted: () => void;

    kalkulatorFilters: {
        activeMaterialIds: string[];
        activeDeviceIds: string[];
    };

    latestCalcSnapshot?: any;

    initializeStore: (targetUid?: string) => void | Promise<void>;
    /** Wechselt die aktive Filiale (CURRENT_BRANCH) und lädt deren Jahresdaten neu */
    setActiveBranch: (branchId: string) => Promise<void>;
    setClientData: (updater: ((prev: ClientDataState) => ClientDataState) | ClientDataState) => void;
    setSettings: (updater: ((prev: SettingsState) => SettingsState) | SettingsState) => void;
    updateSettings: (key: keyof SettingsState, value: any) => void;
    updateNestedSettings: (parentKey: 'global' | 'webdesignPackages' | 'countryOverrides', nestedKey: string, value: any) => void;
    updateNestedClientData: (parentKey: keyof ClientDataState, nestedKey: string, value: any) => void;
    syncNavigation: () => void;
    addTableRow: (key: keyof ClientDataState, template: any) => void;
    updateTableRow: (key: keyof ClientDataState, index: number, field: string | Record<string, any>, value?: any) => void;
    deleteTableRow: (key: keyof ClientDataState, index: number) => void;
    setShowFormulas: (show: boolean | ((prev: boolean) => boolean)) => void;
    setZoomLevel: (level: number | ((prev: number) => number)) => void;
    setKalkulatorFilters: (filters: Partial<AppState['kalkulatorFilters']>) => void;
    exportClientData: () => void;
    importClientData: (file: File) => Promise<string>;
    setInsiderModus: (isInsider: boolean) => void;
    setVatExempt: (isExempt: boolean) => void;
    updateBwaData: (key: string, value: any) => void;
    addPrognoseZeile: (defaults?: { lohn: number, faktor: number }) => void;
    updatePrognoseZeile: (index: number, field: string, value: any) => void;
    removePrognoseZeile: (id: string) => void;
    setHj2Freigabe: (isFreigegeben: boolean, lockContext?: {
        lohnfaktorAktiv?: number;
        unternehmerFreeze?: FrozenUnternehmerStammdatenHJ1;
    }) => void;
    setHj1SperreGlobal: (isGesperrt: boolean, lockContext?: {
        lohnfaktorAktiv?: number;
        unternehmerFreeze?: FrozenUnternehmerStammdatenHJ1;
    }) => void;
    resetFreischaltung: () => void;
    updateMitarbeiterLeistung: (employeeId: string, data: any) => void;
    initializeMitarbeiterLeistung: (employeeId: string) => void;
    updateMonatsDatenFeld: (employeeId: string, halbjahr: 'halbjahr1' | 'halbjahr2', monatIndex: number, field: keyof MonatsDaten, value: any) => void;
    updateFrozenStammdatenHJ1: (employeeId: string, partial: Partial<FrozenStammdatenHJ1>) => void;
    setTeamBonusMode: (mode: TeamBonusMode) => void;
    setTeamBonusPoolAmount: (amount: number) => void;
    toggleTeamBonusMember: (memberId: string) => void;
    setTeamBonusSelectedIds: (ids: string[]) => void;
    applyTeamBonusShares: () => void;
    addTeamMember: (member: Omit<TeamMember, 'id'>) => void;
    addToTeamRegistry: (member: Omit<TeamMember, 'id'>) => void;
    authorizeTeamMember: (id: string) => void;
    deauthorizeTeamMember: (id: string) => void;
    updateTeamMember: (member: TeamMember) => void;
    deleteTeamMember: (id: string) => void;
    updateAppSettings: (settings: Partial<AppSettings>) => void;
    updateGesamtleistungMonat: (index: number, field: string, value: any) => void;
    updateGesamtleistungSetting: (field: string, value: number) => void;
    updateUrlaubsverlustMonat: (index: number, field: 'urlaub' | 'krankheit', value: number) => void;
    updateULBasisdaten: (field: string, value: string | number) => void;
    updateULMonat: (halbjahr: 'ulHalbjahr1' | 'ulHalbjahr2', index: number, field: string, value: any) => void;
    setCostAdjustmentRequired: (isRequired: boolean) => void;
    updateCachedUnternehmerMetrics: (metrics: any) => void;
    undo: () => void;
    redo: () => void;
    captureState: () => void;
    createSnapshot: (name: string) => void;
    loadSnapshot: (id: string) => void;
    deleteSnapshot: (id: string) => void;
    
    initStaffDrafts: () => void;
    updateExistingStaffDraft: (employeeId: string, field: string, value: any) => void;
    /** Mehrere Bestands-Entwürfe atomar anlegen/überschreiben (z.B. Produktiv + Unprod. Anteil als Paar). */
    seedExistingStaffDrafts: (items: Array<{ id: string; inputs: Record<string, any> }>) => void;
    commitDraftState: () => void;
    resetStaffDraft: (employeeId: string) => void;

    addNewStaffDraft: () => void;
    updateNewStaffDraft: (id: string, field: string, value: any) => void;
    removeNewStaffDraft: (id: string) => void;

    addEntrepreneurDraft: () => void;
    updateEntrepreneurDraft: (id: string, field: string, value: any) => void;
    removeEntrepreneurDraft: (id: string) => void;

    addClientMasterDraft: () => void;
    updateClientMasterDraft: (id: string, field: string, value: any) => void;
    removeClientMasterDraft: (id: string) => void;

    addCostDraft: () => void;
    updateCostDraft: (id: string, field: string, value: any) => void;
    removeCostDraft: (id: string) => void;

    addOpeningHoursDraft: () => void;
    updateOpeningHoursDraft: (id: string, field: string, value: any) => void;
    removeOpeningHoursDraft: (id: string) => void;

    setDraftLockStatus: (
        category: 'existingStaff' | 'newStaff' | 'entrepreneur' | 'clientMaster' | 'costs' | 'openingHours',
        id: string,
        isLocked: boolean,
        coachName?: string
    ) => Promise<void>;

    setPerformanceLock: (
        domain: 'mitarbeiter' | 'unternehmer' | 'gesamt',
        targetId: string | null,
        isLocked: boolean,
        metricsSnapshot?: Record<string, any>,
        adminId?: string,
        lockContext?: { halbjahrKey: 'halbjahr1' | 'halbjahr2'; monatIndex: number }
    ) => Promise<void>;

    cloudWriteLock: boolean;

    isDirty: boolean;
    setIsDirty: (dirty: boolean) => void;

    executeJahreswechsel: () => void;
    rolloverConfirm: boolean;
    setRolloverConfirm: (val: boolean) => void;
}